import * as Sentry from '@sentry/browser'

import { FLUSH, PAUSE, PERSIST, PURGE, REGISTER, REHYDRATE, persistStore } from 'redux-persist'
import { PayloadAction, combineReducers, configureStore, getDefaultMiddleware } from '@reduxjs/toolkit'
import authentication, { initialState as authenticationInitialState } from 'redux/slices/authentication'
import chat, { initialState as chatInitialState } from './slices/chat'
import globalError, { initialState as globalErrorInitialState } from './slices/globalError'
import globalModal, { initialState as globalModalInitialState } from './slices/globalModal'
import interaction, { initialState as interactionInitialState } from 'redux/slices/interaction'
import liveShow, { initialState as liveShowInitialState } from 'redux/slices/liveShow'
import liveUserActions, { initialState as liveUserActionsInitialState } from './slices/liveUserActions'
import loginModal, { initialState as loginModalInitialState } from 'redux/slices/loginModal'
import messagingBanner, { initialState as messagingBannerInitialState } from 'redux/slices/messagingBanner'
import passTier, { initialState as passTierInitialState } from 'redux/slices/passTier'
import payment, { initialState as paymentInitialState } from './slices/payment'
import paywall, { initialState as paywallInitialState } from 'redux/slices/paywall'
import passes, { initialState as passesInitialState } from 'redux/slices/passes'
import priorityList, { initialState as priorityListInitialState } from './slices/priorityList'
import cities, { initialState as citiesState } from './slices/cities'
import showNotes, { initialState as showNotesInitialState } from './slices/showNotes'
import shows, { initialState as showsInitialState } from './slices/shows'
import user, { initialState as userInitialState } from 'redux/slices/user'
import viewer, { initialState as viewerInitialState } from 'redux/slices/viewer'
import wordFlow, { initialState as wordFlowInitialState } from 'redux/slices/wordFlow'
import timers, { initialState as timersInitialState } from 'redux/slices/timers'
import curtain, { initialState as curtainInitialState } from 'redux/slices/curtain'
import accessCode, { initialState as accessCodeState } from 'redux/slices/accessCode'

import { ThunkAction } from 'redux-thunk'
import createSentryMiddleware from 'redux-sentry-middleware'

export const appInitialState = {
    user: userInitialState,
    viewer: viewerInitialState,
    loginModal: loginModalInitialState,
    globalModal: globalModalInitialState,
    liveShow: liveShowInitialState,
    interaction: interactionInitialState,
    globalError: globalErrorInitialState,
    paywall: paywallInitialState,
    passes: passesInitialState,
    chat: chatInitialState,
    cities: citiesState,
    payment: paymentInitialState,
    priorityList: priorityListInitialState,
    liveUserActions: liveUserActionsInitialState,
    showNotes: showNotesInitialState,
    shows: showsInitialState,
    messagingBanner: messagingBannerInitialState,
    authentication: authenticationInitialState,
    passTier: passTierInitialState,
    wordFlow: wordFlowInitialState,
    timers: timersInitialState,
    curtain: curtainInitialState,
    accessCode: accessCodeState,
}

export const reducers = {
    user,
    viewer,
    loginModal,
    globalModal,
    liveShow,
    interaction,
    globalError,
    paywall,
    passes,
    chat,
    cities,
    payment,
    priorityList,
    liveUserActions,
    showNotes,
    shows,
    messagingBanner,
    authentication,
    passTier,
    wordFlow,
    timers,
    curtain,
    accessCode,
}

export const rootReducer = combineReducers(reducers)

const isStaging = window.location.host === 'dev.cadenza-dev.com' // TODO: move this into an env variable

const store = configureStore({
    reducer: rootReducer,
    middleware: [
        ...getDefaultMiddleware({
            serializableCheck: {
                ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
            },
        }),
        createSentryMiddleware(Sentry, {}),
    ] as const, // prevent this from becoming just `Array<Middleware>`
    devTools: process.env.NODE_ENV !== 'production' || isStaging,
})

export const persistor = persistStore(store)

export type RootState = ReturnType<typeof rootReducer>

export type AppThunk = ThunkAction<Promise<void>, RootState, never, PayloadAction<any>>

export type AppDispatch = typeof store.dispatch

export default store
